import React, { useState } from 'react';
import { Plus, Edit, Trash2, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import { categoriesAPI, tagsAPI, userAPI } from '../utils/api';

const Settings = ({ categories, tags, userProfile, onCategoryUpdate, onTagUpdate, onUserUpdate }) => {
  const [activeTab, setActiveTab] = useState('categories');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingTag, setEditingTag] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', color: '#6366f1', icon: 'folder' });
  const [newTag, setNewTag] = useState({ name: '', color: '#64748b' });
  const [userSettings, setUserSettings] = useState({
    birthDate: userProfile?.birthDate || '',
    name: userProfile?.name || '',
    preferences: userProfile?.preferences || {}
  });

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      await categoriesAPI.create(newCategory);
      setNewCategory({ name: '', description: '', color: '#6366f1', icon: 'folder' });
      onCategoryUpdate();
      toast.success('Category created!');
    } catch (error) {
      toast.error('Failed to create category');
    }
  };

  const handleUpdateCategory = async (id, data) => {
    try {
      await categoriesAPI.update(id, data);
      setEditingCategory(null);
      onCategoryUpdate();
      toast.success('Category updated!');
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Delete this category? Associated todos will become uncategorized.')) return;
    try {
      await categoriesAPI.delete(id);
      onCategoryUpdate();
      toast.success('Category deleted!');
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const handleCreateTag = async (e) => {
    e.preventDefault();
    try {
      await tagsAPI.create(newTag);
      setNewTag({ name: '', color: '#64748b' });
      onTagUpdate();
      toast.success('Tag created!');
    } catch (error) {
      toast.error('Failed to create tag');
    }
  };

  const handleUpdateTag = async (id, data) => {
    try {
      await tagsAPI.update(id, data);
      setEditingTag(null);
      onTagUpdate();
      toast.success('Tag updated!');
    } catch (error) {
      toast.error('Failed to update tag');
    }
  };

  const handleDeleteTag = async (id) => {
    if (!window.confirm('Delete this tag?')) return;
    try {
      await tagsAPI.delete(id);
      onTagUpdate();
      toast.success('Tag deleted!');
    } catch (error) {
      toast.error('Failed to delete tag');
    }
  };

  const handleUpdateUserProfile = async (e) => {
    e.preventDefault();
    try {
      await userAPI.updateProfile(userSettings);
      onUserUpdate();
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const colorOptions = [
    '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', 
    '#f59e0b', '#10b981', '#06b6d4', '#64748b'
  ];

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>Settings</h1>
      </div>

      <div className="settings-tabs">
        <button 
          className={`tab ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveTab('categories')}
        >
          Categories
        </button>
        <button 
          className={`tab ${activeTab === 'tags' ? 'active' : ''}`}
          onClick={() => setActiveTab('tags')}
        >
          Tags
        </button>
        <button 
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
      </div>

      {activeTab === 'categories' && (
        <div className="settings-content">
          <div className="settings-section">
            <h2>Create New Category</h2>
            <form onSubmit={handleCreateCategory} className="settings-form">
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Category name"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  required
                />
                <input
                  type="text"
                  placeholder="Description"
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="color-picker">
                  <label>Color:</label>
                  {colorOptions.map(color => (
                    <button
                      key={color}
                      type="button"
                      className={`color-option ${newCategory.color === color ? 'selected' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setNewCategory({ ...newCategory, color })}
                    />
                  ))}
                </div>
                <button type="submit" className="btn-primary">
                  <Plus size={18} />
                  Create Category
                </button>
              </div>
            </form>
          </div>

          <div className="settings-section">
            <h2>Existing Categories</h2>
            <div className="items-list">
              {categories.map(category => (
                <div key={category.id} className="item-card">
                  {editingCategory?.id === category.id ? (
                    <div className="item-edit">
                      <input
                        type="text"
                        value={editingCategory.name}
                        onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                      />
                      <div className="color-picker">
                        {colorOptions.map(color => (
                          <button
                            key={color}
                            type="button"
                            className={`color-option ${editingCategory.color === color ? 'selected' : ''}`}
                            style={{ backgroundColor: color }}
                            onClick={() => setEditingCategory({ ...editingCategory, color })}
                          />
                        ))}
                      </div>
                      <div className="item-actions">
                        <button 
                          onClick={() => handleUpdateCategory(category.id, editingCategory)}
                          className="btn-primary-small"
                        >
                          <Save size={16} />
                        </button>
                        <button 
                          onClick={() => setEditingCategory(null)}
                          className="btn-secondary-small"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="item-info">
                        <div 
                          className="item-color" 
                          style={{ backgroundColor: category.color }}
                        />
                        <div>
                          <h4>{category.name}</h4>
                          {category.description && <p>{category.description}</p>}
                        </div>
                      </div>
                      <div className="item-actions">
                        <button 
                          onClick={() => setEditingCategory(category)}
                          className="btn-icon"
                        >
                          <Edit size={18} />
                        </button>
                        <button 
                          onClick={() => handleDeleteCategory(category.id)}
                          className="btn-icon danger"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'tags' && (
        <div className="settings-content">
          <div className="settings-section">
            <h2>Create New Tag</h2>
            <form onSubmit={handleCreateTag} className="settings-form">
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Tag name"
                  value={newTag.name}
                  onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                  required
                />
                <div className="color-picker">
                  <label>Color:</label>
                  {colorOptions.map(color => (
                    <button
                      key={color}
                      type="button"
                      className={`color-option ${newTag.color === color ? 'selected' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setNewTag({ ...newTag, color })}
                    />
                  ))}
                </div>
                <button type="submit" className="btn-primary">
                  <Plus size={18} />
                  Create Tag
                </button>
              </div>
            </form>
          </div>

          <div className="settings-section">
            <h2>Existing Tags</h2>
            <div className="tags-grid">
              {tags.map(tag => (
                <div key={tag.id} className="tag-item" style={{ backgroundColor: `${tag.color}20`, borderColor: tag.color }}>
                  {editingTag?.id === tag.id ? (
                    <div className="tag-edit">
                      <input
                        type="text"
                        value={editingTag.name}
                        onChange={(e) => setEditingTag({ ...editingTag, name: e.target.value })}
                      />
                      <button onClick={() => handleUpdateTag(tag.id, editingTag)}>
                        <Save size={14} />
                      </button>
                      <button onClick={() => setEditingTag(null)}>Cancel</button>
                    </div>
                  ) : (
                    <>
                      <span style={{ color: tag.color }}>{tag.name}</span>
                      <div className="tag-actions">
                        <button onClick={() => setEditingTag(tag)}>
                          <Edit size={14} />
                        </button>
                        <button onClick={() => handleDeleteTag(tag.id)}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'profile' && (
        <div className="settings-content">
          <div className="settings-section">
            <h2>User Profile</h2>
            <form onSubmit={handleUpdateUserProfile} className="settings-form">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  placeholder="Your name"
                  value={userSettings.name}
                  onChange={(e) => setUserSettings({ ...userSettings, name: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Birth Date</label>
                <input
                  type="date"
                  value={userSettings.birthDate}
                  onChange={(e) => setUserSettings({ ...userSettings, birthDate: e.target.value })}
                />
                <p className="form-help">Set your birth date to track your age in the sidebar</p>
              </div>

              <button type="submit" className="btn-primary">
                <Save size={18} />
                Save Profile
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
